import { memo, useState } from "react";
import { type CadTabProps } from "./CadTab";

import classes from "./EmptyCadTab.module.less";
import { userIsLoggedIn } from "../../../../utils/roles";
import GridGappedContainer, {
  GridGappedContainerDirection,
  GridGappedContainerSize,
} from "../../../../common/components/GridGappedContainer/GridGappedContainer";
import { LoadingButton } from "../../../../common/components/Button/Button";
import { Icon } from "../../../../common/components/icon/Icon";
import LoginPopup from "../../../../Login/components/LoginPopup";
import ResetPasswordPopup from "../../../../Login/components/ResetPasswordPopup";
import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import { isLoginPopupInitiallyOpen } from "../../../../Login/loginUtils";
import { IconType } from "../../../../common/components/icon/IconType";
import { IconSize } from "../../../../common/components/icon/IconSize";
import { Trans, useTranslation } from "react-i18next";
import { requestCadNotification } from "./api/requestCadNotification";
import { trackPdpRequestCADNotify, trackPdpRequestCADNotifyResult } from "~/feature/Analytics/ga/ga";

const EmptyCadTab = (props: CadTabProps) => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(props.product.customerRequestedCADAvailability);
  const [submitting, setSubmitting] = useState(false);

  const isLoggedIn = userIsLoggedIn();

  const {
    val: loginPopupOpen,
    setTrue: openLogin,
    setFalse: closeLogin,
  } = useBooleanState(() => isLoginPopupInitiallyOpen());
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);

  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const subscribe = async () => {
    trackPdpRequestCADNotify();
    setSubmitting(true);
    try {
      await requestCadNotification(props.product.code);
      trackPdpRequestCADNotifyResult(true);
    } catch {
      trackPdpRequestCADNotifyResult(false);
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  const cta = (
    <LoadingButton
      startIcon={<Icon type={IconType.Envelope} />}
      onClick={isLoggedIn ? subscribe : openLogin}
      inFlight={submitting}
    >
      {t(`page.pdp.cad.sorry.cta.${isLoggedIn ? "notify" : "login"}`)}
    </LoadingButton>
  );

  let content;
  if (!submitted) {
    content = (
      <GridGappedContainer
        gapSize={GridGappedContainerSize.S_16}
        direction={GridGappedContainerDirection.COLUMN}
        className={classes.contentContainer}
      >
        <h2 className={classes.title}>{t("page.pdp.cad.sorry.title")}</h2>
        <p className={classes.text}>{t(`page.pdp.cad.sorry.content.${isLoggedIn ? "user" : "anonymous"}`)}</p>
        {cta}
      </GridGappedContainer>
    );
  } else {
    const designation = props.product.designation;
    content = (
      <GridGappedContainer
        gapSize={GridGappedContainerSize.S_32}
        direction={GridGappedContainerDirection.COLUMN}
        className={classes.contentContainer}
      >
        <Icon size={IconSize.FONT_64} type={IconType.CheckCircle} className={"f-green"} />
        <Trans i18nKey={"page.pdp.cad.sorry.success"}>
          Thank you for your interest. We will notify you as soon as the CAD file for <b>{{ designation }}</b>{" "}
          is available.
        </Trans>
      </GridGappedContainer>
    );
  }

  return (
    <>
      <div className={classes.root}>{content}</div>
      {loginPopupOpen && (
        <LoginPopup
          initialEmail={""}
          onResetClick={onResetClick}
          close={closeLogin}
          alternativeGAAction={"Sign In CAD Tab"}
        />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
    </>
  );
};

export default memo(EmptyCadTab);
